import React from 'react';
import { Calendar, Clock, AlertCircle, User, Edit2, Trash2, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const RemindsList = ({ reminds, onEdit, onDelete, onComplete, currentUser }) => {
  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'urgente':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'alta':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'normal':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'baja':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800';
      case 'realizado':
        return 'bg-green-100 text-green-800';
      case 'listo':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'Pendiente';
      case 'en_proceso':
        return 'En Proceso';
      case 'realizado':
        return 'Realizado';
      case 'listo':
        return 'Listo';
      default:
        return estado;
    }
  };

  const formatFechaHora = (fechaHora) => {
    try {
      const fecha = new Date(fechaHora);
      return format(fecha, "d 'de' MMMM, yyyy 'a las' HH:mm", { locale: es });
    } catch (error) {
      return fechaHora;
    }
  };

  if (!reminds || reminds.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No hay reminds</h3>
        <p className="mt-1 text-sm text-gray-500">
          Comienza creando un nuevo remind para tus clientes.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {reminds.map((remind) => (
        <div
          key={remind.id}
          className={`bg-white rounded-lg shadow-md border-l-4 ${
            remind.prioridad === 'urgente' ? 'border-red-500' :
            remind.prioridad === 'alta' ? 'border-orange-500' :
            remind.prioridad === 'normal' ? 'border-blue-500' :
            'border-gray-500'
          } hover:shadow-lg transition-shadow`}
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {remind.nombre_cliente}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(remind.prioridad)}`}>
                    <AlertCircle size={12} className="mr-1" />
                    {remind.prioridad?.toUpperCase()}
                  </span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(remind.estado)}`}>
                    {getEstadoLabel(remind.estado)}
                  </span>
                </div>
              </div>
            </div>

            {/* Descripción */}
            <p className="text-sm text-gray-600 mb-3 line-clamp-3">
              {remind.descripcion}
            </p>

            {/* Fecha y Hora */}
            <div className="flex items-center text-sm text-gray-500 mb-2">
              <Calendar size={14} className="mr-2" />
              <span>{formatFechaHora(remind.fecha_hora)}</span>
            </div>

            {/* Agentes Asignados (Múltiples) */}
            {remind.agente_asignado && Array.isArray(remind.agente_asignado) && remind.agente_asignado.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <User size={14} className="mr-2" />
                  <span className="font-medium">Agentes ({remind.agente_asignado.length}):</span>
                </div>
                <div className="flex flex-wrap gap-1 ml-5">
                  {remind.agente_asignado.map((agentEmail, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700"
                      title={agentEmail}
                    >
                      {agentEmail.split('@')[0]}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Fotos */}
            {remind.fotos && remind.fotos.length > 0 && (
              <div className="flex items-center text-sm text-gray-500 mb-3">
                <ImageIcon size={14} className="mr-2" />
                <span>{remind.fotos.length} foto{remind.fotos.length > 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Acciones */}
            <div className="flex justify-end space-x-2 pt-3 border-t">
              {remind.estado !== 'realizado' && remind.estado !== 'listo' && (
                <button
                  onClick={() => onComplete(remind)}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Marcar como completado"
                >
                  <CheckCircle size={18} />
                </button>
              )}
              {/* Solo el creador puede editar y eliminar */}
              {currentUser && remind.created_by === currentUser.email && (
                <>
                  <button
                    onClick={() => onEdit(remind)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => onDelete(remind)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={18} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RemindsList;
